/**
 * pages/Home.tsx — the primary landed surface for a signed-in user.
 *
 * Layout comes from <AppShell current="home">. This page renders
 * ONLY the main-content slot: balance hero, quick-action row, and
 * recent-activity preview. Money-moving CTAs open the
 * "Approve on Mobile" modal (web sessions are 403 on mutations by
 * design).
 *
 * Data:
 *   - GET /v1/balance  → hero balance + sendable/pending/held tiles
 *   - GET /v1/activity → recent 10 transactions preview
 *   (GET /v1/me is fetched by AppShell for the topbar handle.)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  Modal,
  Money,
  Skeleton,
  StatusChip,
  Table,
  type TableColumn,
} from "../components";
import { fetchActivity, fetchBalance, moneyMinor } from "../api/wallet";
import type { ActivityItem, BalanceOut } from "../api/wallet";
import { ApiClientError } from "../api/client";
import { AppShell } from "./AppShell";
import styles from "./Home.module.css";

interface ApprovalRequest {
  action: string;
  description: string;
}

export function Home() {
  const [balance, setBalance] = useState<BalanceOut | null>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const results = await Promise.allSettled([
        fetchBalance(),
        fetchActivity(null, 10),
      ]);
      if (cancelled) return;
      const [balR, actR] = results;
      if (balR.status === "fulfilled") setBalance(balR.value);
      if (actR.status === "fulfilled") setActivity(actR.value.data);

      const errs = results
        .filter((r): r is PromiseRejectedResult => r.status === "rejected")
        .map((r) =>
          r.reason instanceof ApiClientError
            ? r.reason.message
            : String(r.reason),
        );
      if (errs.length > 0) setLoadError(errs[0]);
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const openApproval = useCallback((action: string, description: string) => {
    setApproval({ action, description });
  }, []);

  const columns = useMemo<TableColumn<ActivityItem>[]>(
    () => [
      {
        key: "when",
        label: "Date",
        width: 96,
        render: (r) => formatDate(r.occurred_at),
      },
      {
        key: "cp",
        label: "Counterparty",
        render: (r) => (
          <span className={styles.cpCell}>
            <Avatar
              initials={r.initials || (r.title || "?").slice(0, 2).toUpperCase()}
              size={28}
            />
            <span className={styles.cpText}>
              <span className={styles.cpTitle}>{r.title}</span>
              {r.subtitle ? (
                <span className={styles.cpSubtitle}>{r.subtitle}</span>
              ) : null}
            </span>
          </span>
        ),
      },
      {
        key: "amount",
        label: "Amount",
        align: "right",
        width: 160,
        render: (r) => {
          const minor = moneyMinor(r.amount);
          const failed = r.status === "failed" || r.status === "reversed";
          return (
            <Money
              value={minor}
              asset={r.amount.asset}
              size="amount"
              intent={failed ? "muted" : minor > 0n ? "credit" : "default"}
            />
          );
        },
      },
      {
        key: "status",
        label: "Status",
        width: 120,
        render: (r) => <StatusChip status={r.status} />,
      },
    ],
    [],
  );

  return (
    <AppShell current="home">
      {loadError && (
        <Card className={styles.errorCard}>
          <span className={styles.errorLine}>
            Couldn’t load part of the page — {loadError}. Retry?
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </Card>
      )}

      {/* Balance hero */}
      <section className={styles.heroSection}>
        <p className={styles.eyebrow}>Available balance</p>
        <h1 className={styles.srOnlyHeading}>Home — Available balance</h1>
        {balance ? (
          <Money
            value={moneyMinor(balance.available)}
            asset={balance.available.asset}
            size="hero"
          />
        ) : (
          <Skeleton variant="block" height={56} width={240} />
        )}
        <div className={styles.balanceTiles}>
          <BalanceTile
            label="Sendable"
            minor={balance ? moneyMinor(balance.sendable) : null}
            asset={balance?.sendable.asset || "USD"}
            loading={!balance}
          />
          <BalanceTile
            label="Pending"
            minor={balance ? moneyMinor(balance.pending) : null}
            asset={balance?.pending.asset || "USD"}
            loading={!balance}
          />
          <BalanceTile
            label="Held"
            minor={balance ? moneyMinor(balance.held) : null}
            asset={balance?.held.asset || "USD"}
            loading={!balance}
          />
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={() =>
              openApproval(
                "Send money",
                "Move funds to another AsterCash user or an external account.",
              )
            }
          >
            Send
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              openApproval(
                "Add money",
                "Top up your balance from a linked funding source.",
              )
            }
          >
            Add money
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              openApproval(
                "Request money",
                "Send a payment request to another AsterCash handle.",
              )
            }
          >
            Request
          </Button>
        </div>
      </section>

      {/* Recent activity preview */}
      <section className={styles.activitySection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Recent activity</h2>
          <a href="/activity" className={styles.linkAction}>
            View all →
          </a>
        </div>
        <Card padding={0}>
          {activity === null ? (
            <div className={styles.tableSkeleton}>
              <Skeleton variant="row" />
              <Skeleton variant="row" />
              <Skeleton variant="row" />
              <Skeleton variant="row" />
            </div>
          ) : activity.length === 0 ? (
            <EmptyState
              title="No activity yet"
              body="Your recent transactions will appear here as they clear."
            />
          ) : (
            <>
              <div className={styles.desktopOnly}>
                <Table
                  columns={columns}
                  rows={activity}
                  rowKey={(r) => r.id}
                  empty={
                    <EmptyState
                      title="No activity yet"
                      body="Your transactions will appear here."
                    />
                  }
                />
              </div>
              <div className={styles.activityList}>
                {activity.map((r) => (
                  <ActivityCell key={r.id} row={r} />
                ))}
              </div>
            </>
          )}
        </Card>
      </section>

      <Modal
        open={approval !== null}
        onDismiss={() => setApproval(null)}
        title={approval ? `${approval.action} needs your phone` : "This needs your phone"}
        blurScrim
        showClose={false}
      >
        <p className={styles.modalBody}>
          {approval?.description ||
            "Confirm this action on your registered mobile device."}
        </p>
        <p className={styles.modalRule}>
          Web sessions are <strong>read-only</strong>. Money-moving actions
          and authentication changes must be approved from your registered
          mobile device — even if this browser has already signed in.
        </p>
        <p className={styles.modalStatus}>
          The AsterCash mobile app is not yet in the App Store. Send, Add
          money, and Request will work here once the mobile app ships —
          your registered phone will receive the approval request and, on
          confirm, the transfer completes end to end.
        </p>
        <p className={styles.modalStatus}>
          Until then, this is a preview of the flow. Nothing has been sent
          to any device. Email{" "}
          <a href="mailto:support@astercash.com" className={styles.modalMail}>
            support@astercash.com
          </a>{" "}
          if you need to move money in the meantime.
        </p>
        <div className={styles.modalCta}>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => setApproval(null)}
          >
            Close
          </Button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components.
// ---------------------------------------------------------------------------

function BalanceTile({
  label,
  minor,
  asset,
  loading,
}: {
  label: string;
  minor: bigint | null;
  asset: string;
  loading: boolean;
}) {
  return (
    <div className={styles.balanceTile}>
      <p className={styles.balanceTileLabel}>{label}</p>
      {loading || minor === null ? (
        <Skeleton variant="text" width="60%" />
      ) : (
        <Money value={minor} asset={asset} size="amount" intent="muted" />
      )}
    </div>
  );
}

function ActivityCell({ row }: { row: ActivityItem }) {
  const minor = moneyMinor(row.amount);
  const failed = row.status === "failed" || row.status === "reversed";
  return (
    <div className={styles.activityItem}>
      <Avatar
        initials={row.initials || (row.title || "?").slice(0, 2).toUpperCase()}
        size={32}
      />
      <div className={styles.activityItemMain}>
        <div className={styles.activityItemTitle}>{row.title}</div>
        <div className={styles.activityItemMeta}>
          <span>{formatDate(row.occurred_at)}</span>
          {row.subtitle ? (
            <>
              <span className={styles.activityItemMetaDot} />
              <span>{row.subtitle}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className={styles.activityItemRight}>
        <Money
          value={minor}
          asset={row.amount.asset}
          size="amount"
          intent={failed ? "muted" : minor > 0n ? "credit" : "default"}
        />
        <StatusChip status={row.status} />
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}
